import * as R from 'ramda'
import * as RE from 'helpers/ramda-ext'
import moment from 'moment'
import { PType, getP } from 'redux-polyglot'
import { createSelector } from 'reselect'
import { ALL_PRODUCTS_SCOPE_URI, ALL_PRODUCTS_TYPE } from 'constants/navigation'
import {
  getCitiesInfoFromCode,
  getFormattedCurrentDay,
  getDateByOffsetAndSaleBeginDate,
  getOffsetsWithFormattedDateList,
  isValidList
} from 'domain/filters'
import { groupBy2Filled } from 'domain/offers'
import { getLabelsFormattedForTracking, getProductPrice } from 'domain/products'
import {
  equalsResource,
  getFirstCatalogAncestor,
  injectLinkTo,
  injectOrder,
  isParent,
  isType,
  isTypeCatalog,
  isTypeNavigation,
  isTypeProduct,
  isTypeSale,
  isValidResource
} from 'domain/resources'
import { createBreadcrumb } from 'domain/scope'
import { toHref } from 'helpers/PathHelper'
import { ascendingSortNumbersArray } from 'helpers/sortNumbers'
import { getResources } from 'store/reducers/resources'
import {
  DEPARTURE_CITIES_FILTERS_ID,
  DEPARTURE_CITIES_FILTERS_KEY,
  DEPARTURE_DATES_FILTERS_ID,
  DEPARTURE_DATES_FILTERS_KEY,
  DURATIONS_FILTERS_ID,
  DURATIONS_FILTERS_KEY
} from 'constants/filters'
import {
  getAllCitiesOfUniverses,
  getAllDeparturesDatesOfUniverses,
  getAllDurationsOfUniverses,
  getAllProductsResource
} from './allProducts'
import {
  getIsHideReferencePriceFeatureEnabled,
  getOperationCode
} from './application'
import {
  getCurrentResource,
  getCurrentResourceCities,
  getCurrentResourceDeparturesDates,
  getCurrentResourceDurations
} from './currentResource'
import {
  getAreFiltersInitialized,
  getDepartureCitiesFiltersFromReducer,
  getFiltersProducts,
  getSelectedDepartureDateOffset,
  getSelectedDepartureDateOffsetFromQuery,
  getSelectedDepartureDatesFlexibilityFromQuery,
  getSelectedDurationsFiltersFromReducer,
  getSelectedTransportation
} from './filters'
import {
  getActiveSaleFiltersURIParams,
  getBeginDate,
  getCities,
  getFavoriteDepartureCityCode,
  getHasOnlyOneUniverse,
  getQueryFiltersParams,
  getSaleResource,
  getSaleSlug,
  getShowSaleFilters
} from './sale'
import {
  GetAllDurations,
  GetAllFilteredProduct,
  GetAllFilteredProductsGroupedByTwo,
  GetBreadcrumb,
  GetBreadcrumbKeysList,
  GetCitiesByKeyReference,
  GetCitiesFromCode,
  GetDatesFromBeginDate,
  GetDepartureDatesByOffsetsReference,
  GetFilteredProductsInParentScope,
  GetFilteredProductsInParentScopeGroupedByTwo,
  GetFilteredProductsInParentScopeWithStock,
  GetFilteredProductsLabelsFormattedForTracking,
  GetFilteredProductsTagsValues,
  GetFiltersElements,
  GetFiltersFromURI,
  GetFirstSubUniverseWithProducts,
  GetFirstSubUniverseWithProductsUrl,
  GetFirstUniverse,
  GetHasProductWithCoordinatesInUniverse,
  GetIsInAllProductsScope,
  GetNavItems,
  GetNbFilteredProductsReferences,
  GetNbFilteredProductsWithTags,
  GetNextProductInScope,
  GetOffsetsByDepartureDatesReference,
  GetPreviousProductInScope,
  GetProductsInParentScope,
  GetSaleResourceResources,
  GetScope,
  GetScopeLinkTo,
  GetSelectedDepartureCitiesFiltersElements,
  GetSelectedDepartureDatesFiltersElements,
  GetSelectedDepartureDatesOffsetsRangeListFromQuery,
  GetSelectedDurationsFiltersElements
} from './scope.types'
import { getUri, getQuery, getFrom } from './router'
import {
  DepartureDateOffset,
  FilteredProductResource,
  FilterElement,
  FilterElements,
  FiltersFromURI,
  NavItem,
  ProductResource,
  Resource,
  Resources,
  UniverseResource
} from 'types/typescript'

export const getScope: GetScope = createSelector(
  getUri,
  getCurrentResource,
  getAllProductsResource,
  getResources,
  (uri, currentResource, allProductsResource, allResources) => {
    if (R.isEmpty(allResources) || !currentResource) { return null }

    if (uri.search(`\\/${ALL_PRODUCTS_SCOPE_URI}(\\/.*)*(\\?.+)?$`) > -1) { return allProductsResource }

    return R.when(
      R.compose(R.not, isTypeCatalog),
      R.when(
        R.complement(isTypeSale),
        getFirstCatalogAncestor(R.__, allResources)
      )
    )(currentResource)
  }
)

export const getIsInAllProductsScope: GetIsInAllProductsScope = createSelector(
  getScope,
  scope => scope && isType(ALL_PRODUCTS_TYPE)(scope)
)

export const getScopeLinkTo: GetScopeLinkTo = createSelector(
  getScope,
  getOperationCode,
  getSaleResource,
  getHasOnlyOneUniverse,
  getResources,
  (scope, opeCode, sale, saleHasOnlyOneUniverse, allResources) => injectLinkTo(
    (scope as Resource),
    opeCode,
    allResources,
    (saleHasOnlyOneUniverse)
      ? sale as Resource
      : scope as Resource
  ) as unknown as Resource
)

export const getFirstUniverse: GetFirstUniverse = createSelector(
  getSaleResource,
  getResources,
  (sale, allResources) => {
    if (sale) {
      return R.compose(
        R.prop(R.__, allResources),
        R.head,
        R.prop('resources')
      )(sale)
    }
    return null
  }
)

export const getFiltersFromURI: GetFiltersFromURI = createSelector(
  getQuery,
  getActiveSaleFiltersURIParams,
  (query, saleFilters) => saleFilters.reduce((filtersFromURI: FiltersFromURI[], key) => {
    if (key in query) {
      filtersFromURI.push({ key: key, values: query[key].split(',') })
    }
    return filtersFromURI
  }, [])
)

export const getProductsInParentScope: GetProductsInParentScope = createSelector(
  getScope,
  getResources,
  getHasOnlyOneUniverse,
  getFirstUniverse,
  getOperationCode,
  getFiltersProducts,
  getAreFiltersInitialized,
  (scope, allResources, saleHasOnlyOneUniverse, firstUniverse, operationCode, filtersProducts, areFiltersInitialized) => {
    const isAllProductsScope = scope && scope.type === ALL_PRODUCTS_TYPE
    const isProductInScope = (parent: Resource): boolean => R.both(isTypeProduct, isParent(parent))

    const order = areFiltersInitialized
      ? R.pipe(
        R.prop('order'),
        R.defaultTo(Number.MAX_SAFE_INTEGER)
      )
      : isAllProductsScope
        ? R.pipe(
          getProductPrice(filtersProducts),
          R.defaultTo({}),
          R.prop('price')
        )
        : R.prop('index')

    const sort = R.sortWith([
      R.ascend(R.prop('outOfStock')),
      R.ascend(order)
    ])

    const getProductsIn = (parent: Resource): ProductResource[] =>
      R.compose(
        sort,
        R.map(
          R.compose(
            injectLinkTo(parent, operationCode, allResources),
            injectOrder(filtersProducts)
          )
        ),
        R.filter(isProductInScope(parent)),
        R.values
      )(allResources)

    if (!scope) {
      return []
    }

    if (isTypeSale(scope) && saleHasOnlyOneUniverse && firstUniverse) {
      return getProductsIn(firstUniverse)
    }

    return getProductsIn(scope)
  }
)

// type GetFilteredProductsInParentScope = State => Array<ProductResourceWithPosition>
export const getFilteredProductsInParentScope: GetFilteredProductsInParentScope = createSelector(
  getProductsInParentScope,
  getFiltersFromURI,
  getFiltersProducts,
  getShowSaleFilters,
  getAreFiltersInitialized,
  getIsHideReferencePriceFeatureEnabled,
  getSelectedTransportation,
  (products, filters, filtersProducts, showFilters, areFiltersInitialized, shouldHideReferencePrice, selectedTransportation) => (
    (!showFilters || (R.isEmpty(filters) && !selectedTransportation) || !areFiltersInitialized)
      ? products
      : products.filter(product => filtersProducts[product.key])
  ).map((p, index) => {
    const product = { ...p, position: index + 1 }

    if (!shouldHideReferencePrice || !p?.ecomm?.price) return product

    return { ...product, ecomm: { ...p.ecomm, price: { ...p.ecomm.price, msrp: null } } }
  })
)

export const getHasProductWithCoordinatesInUniverse: GetHasProductWithCoordinatesInUniverse = createSelector(
  getFilteredProductsInParentScope,
  filteredProductsInParentScope =>
    filteredProductsInParentScope.some(
      resource =>
        resource?.ecomm?.coordinates !== undefined &&
        resource?.ecomm?.coordinates?.latitude !== 0 &&
        resource?.ecomm?.coordinates?.longitude !== 0
    )
)

export const getNbFilteredProductsReferences: GetNbFilteredProductsReferences = createSelector(
  getFilteredProductsInParentScope,
  R.length
)

export const getNbFilteredProductsWithTags: GetNbFilteredProductsWithTags = createSelector(
  getFilteredProductsInParentScope,
  (filteredProducts) => (
    filteredProducts.reduce((nbProducts, product) => (
      product.ecomm.tags && product.ecomm.tags.length > 0
        ? nbProducts + 1
        : nbProducts
    ), 0)
  )
)

export const getFilteredProductsTagsValues: GetFilteredProductsTagsValues = createSelector(
  getFilteredProductsInParentScope,
  (filteredProducts) => (
    filteredProducts.reduce((tagsDisplayed: string[], product) => {
      const productTags = product.ecomm.tags

      if (productTags?.length > 0) {
        return [...tagsDisplayed, ...productTags]
      }

      return tagsDisplayed
    }, [])
  )
)

export const getFilteredProductsLabelsFormattedForTracking: GetFilteredProductsLabelsFormattedForTracking = createSelector(
  getFilteredProductsInParentScope,
  (filteredProducts) => {
    const allLabels = filteredProducts.flatMap((product) => product.ecomm.labels)
    return getLabelsFormattedForTracking(allLabels)
  }
)

const getFilteredProductsInParentScopeWithStock: GetFilteredProductsInParentScopeWithStock = createSelector(
  getFilteredProductsInParentScope,
  R.filter(({ outOfStock }) => !outOfStock)
)

export const getNextProductInScope: GetNextProductInScope = createSelector(
  getCurrentResource,
  getScope,
  getFilteredProductsInParentScopeWithStock,
  getResources,
  getOperationCode,
  (currentResource, scope, products, allResources, operationCode) => R.compose(
    R.when(
      R.both(RE.isNotEmpty, RE.isNotNil),
      injectLinkTo(scope, operationCode, allResources)
    ),
    R.head,
    R.tail,
    R.dropWhile(R.complement(equalsResource(currentResource)))
  )(products)
)

export const getPreviousProductInScope: GetPreviousProductInScope = createSelector(
  getCurrentResource,
  getScope,
  getFilteredProductsInParentScopeWithStock,
  getResources,
  getOperationCode,
  (currentResource, scope, products, allResources, operationCode) => R.compose(
    R.when(
      R.both(RE.isNotEmpty, RE.isNotNil),
      injectLinkTo(scope, operationCode, allResources)
    ),
    R.last,
    R.takeWhile(R.complement(equalsResource(currentResource)))
  )(products)
)

export const getBreadcrumb: GetBreadcrumb = createSelector(
  getResources,
  getCurrentResource,
  getScope,
  getSaleResource,
  getHasOnlyOneUniverse,
  getOperationCode,
  (allResources, currentResource, scope, sale, saleHasOnlyOneUniverse, opeCode) => (
    (isValidResource(currentResource))
      ? createBreadcrumb(currentResource as Resource, saleHasOnlyOneUniverse, scope, sale, allResources, opeCode, true)
      : []
  )
)

export const getBreadcrumbKeysList: GetBreadcrumbKeysList = createSelector(
  getBreadcrumb,
  R.map(R.prop('key'))
)

/** FirstSubUniverseWithProducts ********************************************* */
/** TODO (JAR) : Pour moi, ce n'est pas bon si j'ai un premier enfant qui n'est pas un produit
 *  et un second qui en est un, il ne sera pas sélectionné (il devrait) */
const findFirstSubUniverseWithProducts = R.curry((allResources, scope, operationCode, resource) => {
  const getInAllResources = (id: string): Resource => allResources[id]
  const firstResource = resource?.resources[0]

  if (!firstResource) return undefined

  return R.ifElse(
    R.compose(R.equals('product'), R.prop('type'), getInAllResources),
    R.always(injectLinkTo(scope, operationCode, allResources)(resource)),
    R.compose(findFirstSubUniverseWithProducts(allResources, scope, operationCode), getInAllResources)
  )(firstResource)
})

export const getFirstSubUniverseWithProducts: GetFirstSubUniverseWithProducts = createSelector(
  getResources,
  getScope,
  getOperationCode,
  getCurrentResource,
  findFirstSubUniverseWithProducts
)

export const getfirstSubUniverseWithProductsUrl: GetFirstSubUniverseWithProductsUrl = createSelector(
  getFirstSubUniverseWithProducts,
  getQueryFiltersParams,
  getFrom,
  getQuery,
  (firstSubUniverseWithProducts, filters, from, queryURL) => {
    const query = {
      ...R.path(['linkTo', 'query'], firstSubUniverseWithProducts),
      ...filters
    }

    if (from) query.from = from

    return toHref({ pathname: firstSubUniverseWithProducts?.linkTo?.pathname, query }, queryURL)
  }
)
/** ************************************************************************** */

/** NavItems ***************************************************************** */
const createNavItems = (
  allResources: Resources,
  saleResourcesKeys: string[],
  breadcrumbIds: string[],
  opeCode: string,
  saleSlug: string,
  parentSlug?: string
): NavItem[] => {
  const getInAllResources = (id: string): Resource => allResources[id]

  return R.pipe(
    R.map(getInAllResources),
    R.filter(isTypeNavigation),
    R.map((res: UniverseResource) => ({
      cheapestPrice: R.path(['ecomm', 'cheapestPrice'])(res),
      isSelected: R.contains(res.key, breadcrumbIds),
      key: res.key,
      name: res.content.title,
      pathname: (parentSlug)
        ? `/${opeCode}/${saleSlug}/${parentSlug}/${res.slug}`
        : `/${opeCode}/${saleSlug}/${res.slug}`,
      subUniverses: createNavItems(
        allResources,
        res.resources,
        breadcrumbIds,
        opeCode,
        saleSlug,
        res.slug
      )
    }))
  )(saleResourcesKeys)
}

export const getSaleResourceResources: GetSaleResourceResources = createSelector(
  getSaleResource,
  R.prop('resources')
)

export const getNavItems: GetNavItems = createSelector(
  getResources,
  getSaleResourceResources,
  getBreadcrumbKeysList,
  getOperationCode,
  getSaleSlug,
  createNavItems
)

// filters

export const getDatesFromBeginDate: GetDatesFromBeginDate = createSelector(
  getAllDeparturesDatesOfUniverses,
  getBeginDate,
  getFormattedCurrentDay,
  getOffsetsWithFormattedDateList
)

export const getCitiesFromCode: GetCitiesFromCode = createSelector(
  getFavoriteDepartureCityCode,
  getAllCitiesOfUniverses,
  getCities,
  getCitiesInfoFromCode
)

export const getAllDurations: GetAllDurations = createSelector(
  getAllDurationsOfUniverses,
  getP,
  (allDurations: number[], p: PType) => allDurations.map(duration => ({
    key: duration,
    label: p.t('common.nights', duration)
  }))
)

export const filtersSelectors = {
  getAllDurations,
  getCitiesFromCode,
  getDatesFromBeginDate
}

export const getAllFilteredProduct: GetAllFilteredProduct = createSelector(
  getOperationCode,
  getFiltersProducts,
  getResources,
  (operationCode, filtersProducts, resources) => (
    Object.keys(filtersProducts).reduce((filteredProducts: FilteredProductResource[], filtersProductKey, index) => {
      const linkedProduct: Resource = resources[filtersProductKey]

      if (linkedProduct) {
        const parentKey = (linkedProduct as ProductResource).parent
        const parentResource = resources[parentKey]
        const productWithLinkTo = injectLinkTo(parentResource, operationCode, resources, linkedProduct) as unknown as ProductResource

        filteredProducts.push({ ...productWithLinkTo, position: (index + 1) })
      }

      return filteredProducts
    }, [])
  )
)

export const getAllFilteredProductsGroupedByTwo: GetAllFilteredProductsGroupedByTwo = createSelector(
  getAllFilteredProduct,
  (products) => groupBy2Filled(products) as FilteredProductResource[][]
)

export const getFilteredProductsInParentScopeGroupedByTwo: GetFilteredProductsInParentScopeGroupedByTwo = createSelector(
  getFilteredProductsInParentScope,
  (products) => groupBy2Filled(products) as FilteredProductResource[][]
)

export const getCitiesByKeyReference: GetCitiesByKeyReference = createSelector(
  getCitiesFromCode,
  R.reduce((acc, { key, ...otherProps }) => {
    acc[key] = otherProps
    return acc
  }, {})
)

export const getSelectedDepartureCitiesFiltersElements: GetSelectedDepartureCitiesFiltersElements = createSelector(
  getDepartureCitiesFiltersFromReducer,
  getCurrentResourceCities,
  getCitiesByKeyReference,
  (filters, currentResourcesCities: string[], reference) => (
    filters.reduce((acc: FilterElement[], filter) => {
      if (reference[filter]) {
        acc.push({
          isDisabled: !currentResourcesCities?.includes(filter),
          ref: filter,
          value: reference[filter].label
        })
      }
      return acc
    }, [])
  )
)

export const getDepartureDatesByOffsetsReference: GetDepartureDatesByOffsetsReference = createSelector(
  getDatesFromBeginDate,
  R.reduce((acc, { offset, ...otherProps }) => {
    acc[offset] = otherProps
    return acc
  }, {})
)

export const getSelectedDepartureDatesFiltersElements: GetSelectedDepartureDatesFiltersElements = createSelector(
  getSelectedDepartureDateOffset,
  getCurrentResourceDeparturesDates,
  getDepartureDatesByOffsetsReference,
  (filters, currentResourcesDates: number[], reference) => (
    filters.reduce((acc: FilterElement[], filter: DepartureDateOffset) => {
      if (reference[filter]) {
        acc.push({
          isDisabled: !currentResourcesDates?.includes(filter),
          ref: filter,
          value: reference[filter].date
        })
      }
      return acc
    }, [])
  )
)

export const getSelectedDurationsFiltersElements: GetSelectedDurationsFiltersElements = createSelector(
  getSelectedDurationsFiltersFromReducer,
  getCurrentResourceDurations,
  getP,
  (filters, currentResourcesDurations: number[], p: PType) => ascendingSortNumbersArray(filters).map((filter: number) => ({
    isDisabled: !currentResourcesDurations?.includes(filter),
    ref: filter,
    value: p.t('common.nights', filter)
  }))
)

export const getFiltersElements: GetFiltersElements = createSelector(
  getSelectedDepartureCitiesFiltersElements,
  getSelectedDepartureDatesFiltersElements,
  getSelectedDurationsFiltersElements,
  (cities, dates, durations) => {
    const result: FilterElements[] = []

    if (dates.length) {
      result.push({
        elements: dates,
        typeID: DEPARTURE_DATES_FILTERS_ID,
        typeKey: DEPARTURE_DATES_FILTERS_KEY
      })
    }
    if (durations.length) {
      result.push({
        elements: durations,
        typeID: DURATIONS_FILTERS_ID,
        typeKey: DURATIONS_FILTERS_KEY
      })
    }
    if (cities.length) {
      result.push({
        elements: cities,
        typeID: DEPARTURE_CITIES_FILTERS_ID,
        typeKey: DEPARTURE_CITIES_FILTERS_KEY
      })
    }

    return result
  }
)

/* ----------------------------------------- */

export const getOffsetsByDepartureDatesReference: GetOffsetsByDepartureDatesReference = createSelector(
  getDatesFromBeginDate,
  (departureDatesOffsetsReference) => (
    R.ifElse(
      isValidList,
      R.reduce((availableDatesReference, element) => (
        { ...availableDatesReference, [element.date]: element.offset }
      ), {}),
      R.always({})
    )(departureDatesOffsetsReference)
  )
)

export const getSelectedDepartureDatesOffsetsRangeListFromQuery: GetSelectedDepartureDatesOffsetsRangeListFromQuery = createSelector(
  getBeginDate,
  getSelectedDepartureDateOffsetFromQuery,
  getSelectedDepartureDatesFlexibilityFromQuery,
  getOffsetsByDepartureDatesReference,
  (beginDate, selectedDepartureDateOffset, flexibility, departureDatesReference) => {
    const newDateOffsets: DepartureDateOffset[] = []

    if (selectedDepartureDateOffset) {
      const departureDate = getDateByOffsetAndSaleBeginDate(selectedDepartureDateOffset, beginDate)
      const dateIterator = moment(departureDate).subtract(flexibility, 'day')
      const flexibilityRange = ((flexibility as number) * 2 + 1)

      for (let i = 0; i < flexibilityRange; ++i) {
        const dateKey = dateIterator.format('YYYY-MM-DD')

        if (dateKey in departureDatesReference) {
          const dateOffset = departureDatesReference[dateKey]

          if (!dateIterator.isSame(departureDate)) {
            newDateOffsets.push(dateOffset)
          } else {
            newDateOffsets.unshift(dateOffset)
          }
        }
        dateIterator.add(1, 'day')
      }
    }

    return newDateOffsets
  }
)
